"use client";

import { SearchInput } from "@/components/ui/search-input";
import { ListSkeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

// The scroll container, search box, skeleton/empty/no-match states and
// infinite-scroll sentinel for a searchable list page. Pair it with
// `useSearchableList`, which derives the flags below.
//
// The invariant this component exists to hold: the search box renders outside
// every loading branch. A debounced search flips `loading`, so any layout that
// swaps the input out for a skeleton destroys the focused element on the
// keystroke that triggered it.

/** The slice of `useSearchableList`'s result this component reads. */
export type SearchableListState = {
  search: string;
  setSearch: (value: string) => void;
  debouncedSearch: string;
  loading: boolean;
  isEmptyResult: boolean;
  showSkeleton: boolean;
  showEmptyState: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  sentinelRef: (node: Element | null) => void;
  rootRef: (node: HTMLDivElement | null) => void;
};

export type SearchableListProps = {
  state: SearchableListState;
  /** Plural noun for the "No {label} match …" line, e.g. "chats". */
  label: string;
  placeholder: string;
  /** Search is rendered in the page header instead of above the results. */
  externalSearch?: boolean;
  /** Replaces the results when the list is empty and unfiltered; an external search stays visible. */
  emptyState: ReactNode;
  /**
   * Filter controls (tabs, toggles) rendered under the search box. Like the
   * search box they sit outside every loading branch, so narrowing to an empty
   * result never removes the control that would widen it again. Pair with
   * `useSearchableList`'s `filtered` flag, or the empty state will replace them.
   */
  filters?: ReactNode;
  /**
   * Shown instead of the "No {label} match …" line when the list came back
   * empty with no search query — i.e. a `filters` selection excluded
   * everything. Ignored while a query is active, which is always the better
   * explanation of an empty result.
   */
  noMatchMessage?: ReactNode;
  /** Shown while there is nothing to display yet. */
  skeleton: ReactNode;
  /** Renders between the search box and the results — e.g. a pinned-items section. Hidden with the rest of the body when emptyState is showing. */
  topContent?: ReactNode;
  /** Replaces the default row-list wrapper (e.g. with a card grid). */
  listClassName?: string;
  /** Applied to the centred column; overrides the default `max-w-4xl`. */
  className?: string;
  footer?: ReactNode;
  children: ReactNode;
};

export function SearchableList({
  state,
  label,
  placeholder,
  externalSearch = false,
  emptyState,
  filters,
  noMatchMessage,
  skeleton,
  topContent,
  listClassName,
  className,
  footer,
  children,
}: SearchableListProps) {
  const {
    search,
    setSearch,
    debouncedSearch,
    loading,
    isEmptyResult,
    showSkeleton,
    showEmptyState,
    hasMore,
    isFetchingMore,
    sentinelRef,
    rootRef,
  } = state;

  return (
    <div ref={rootRef} className="flex-1 overflow-y-auto">
      <div className={cn("mx-auto max-w-4xl px-4 py-6 sm:px-10 sm:py-10", className)}>
        {externalSearch && filters ? <div className="mb-5">{filters}</div> : null}
        {showEmptyState ? (
          emptyState
        ) : (
          <div className="flex flex-col gap-5">
            {/* Never move this inside a loading branch — see the note above. */}
            {!externalSearch && (
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={placeholder}
                className="w-full"
              />
            )}
            {topContent}
            {!externalSearch && filters}
            {showSkeleton ? (
              <div role="status" aria-label={`Loading ${label}`} aria-busy="true">{skeleton}</div>
            ) : isEmptyResult ? (
              <p className="py-16 text-center text-sm text-muted-foreground">
                {debouncedSearch ? (
                  <>No {label} match &ldquo;{debouncedSearch}&rdquo;.</>
                ) : (
                  noMatchMessage ?? <>No {label} found.</>
                )}
              </p>
            ) : (
              // Results stay on screen, dimmed, while a search refetch runs.
              <div
                className={cn(
                  listClassName ?? "flex flex-col divide-y divide-border/40",
                  "content-fade-in transition-opacity duration-250 ease-out motion-reduce:transition-none",
                  loading && "opacity-50"
                )}
              >
                {children}
              </div>
            )}
            {hasMore && !loading && !showSkeleton && (
              <div ref={sentinelRef}>
                {isFetchingMore && <ListSkeleton rows={2} />}
              </div>
            )}
          </div>
        )}
        {footer ? <div className="mt-6">{footer}</div> : null}
      </div>
    </div>
  );
}
